import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GetApp as DownloadIcon,
  Public as StateIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';

const counties = [
  {
    name: 'Bergen County',
    recordingOffice: 'Bergen County Clerk',
    address: '1 Bergen County Plaza, Hackensack, NJ 07601',
    phone: '(201) 336-7000',
    website: 'www.co.bergen.nj.us',
    hours: 'Monday-Friday: 8:30 AM - 4:30 PM',
    deedFee: '$25.00',
    mortgageFee: '$45.00',
    specialRequirements: ['Notarization required', 'Two witnesses for deeds'],
  },
  {
    name: 'Middlesex County',
    recordingOffice: 'Middlesex County Clerk',
    address: '75 Bayard Street, New Brunswick, NJ 08901',
    phone: '(732) 745-3040',
    website: 'www.middlesexcountynj.gov',
    hours: 'Monday-Friday: 8:30 AM - 4:00 PM',
    deedFee: '$30.00',
    mortgageFee: '$50.00',
    specialRequirements: ['Electronic recording available', 'Same-day service available'],
  },
  {
    name: 'Essex County',
    recordingOffice: 'Essex County Clerk',
    address: '465 Dr. Martin Luther King Jr. Blvd, Newark, NJ 07102',
    phone: '(973) 621-4921',
    website: 'www.essexclerk.com',
    hours: 'Monday-Friday: 9:00 AM - 4:00 PM',
    deedFee: '$28.00',
    mortgageFee: '$48.00',
    specialRequirements: ['Certified copies available', 'Online search portal'],
  },
  {
    name: 'Hudson County',
    recordingOffice: 'Hudson County Clerk',
    address: '595 Newark Avenue, Jersey City, NJ 07306',
    phone: '(201) 795-6112',
    website: 'www.hudsoncountyclerk.org',
    hours: 'Monday-Friday: 9:00 AM - 5:00 PM',
    deedFee: '$35.00',
    mortgageFee: '$55.00',
    specialRequirements: ['Walk-in service', 'Extended hours on Thursdays'],
  },
  {
    name: 'Union County',
    recordingOffice: 'Union County Clerk',
    address: '2 Broad Street, Elizabeth, NJ 07207',
    phone: '(908) 527-4966',
    website: 'www.ucnj.org',
    hours: 'Monday-Friday: 8:30 AM - 4:30 PM',
    deedFee: '$32.00',
    mortgageFee: '$52.00',
    specialRequirements: ['Appointment preferred', 'Electronic indexing'],
  },
];

const generalRequirements = [
  {
    category: 'Document Requirements',
    items: [
      'Original documents with original signatures',
      'Proper notarization with seal and commission expiration',
      'Complete property legal description',
      'Grantee and grantor information must be accurate',
    ],
  },
  {
    category: 'Fee Requirements',
    items: [
      'Recording fees vary by county and document type',
      'Real estate transfer tax (if applicable)',
      'Documentary stamp tax requirements',
      'Additional fees for expedited service',
    ],
  },
  {
    category: 'Filing Procedures',
    items: [
      'Documents must be filed during business hours',
      'Some counties offer electronic recording',
      'Certified copies available for additional fee',
      'Same-day recording available in most counties',
    ],
  },
];

const NJRecordingRequirements: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>NJ Recording Requirements by County | Professional Resources | MAD Title Agency</title>
        <meta
          name="description"
          content="Comprehensive guide to recording requirements, fees, and procedures for all 21 New Jersey counties. Essential resource for title professionals."
        />
        <meta
          name="keywords"
          content="New Jersey recording requirements, NJ county clerk offices, recording fees, title professional resources, document recording procedures"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            NJ Recording Requirements
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Resource"
                sx={{
                  backgroundColor: '#059669',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                NJ Recording Requirements by County
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Complete guide to recording requirements, fees, and procedures for all 21 New Jersey counties
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download PDF Guide
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <StateIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  21 Counties Covered
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Updated Monthly
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Quick Reference Alert */}
        <Alert severity="info" sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Quick Reference Guide
          </Typography>
          This resource is updated monthly to reflect current recording fees and requirements. 
          Always verify requirements with the specific county clerk's office before recording.
        </Alert>

        {/* General Requirements */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            General Recording Requirements
          </Typography>
          <Grid container spacing={4}>
            {generalRequirements.map((section, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#059669' }}>
                      {section.category}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {section.items.map((item, itemIndex) => (
                        <Typography component="li" key={itemIndex} sx={{ mb: 1 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* County-Specific Information */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            County-Specific Requirements
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Select a county below for detailed recording information, fees, and special requirements.
          </Typography>

          {counties.map((county, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {county.name}
                  </Typography>
                  <Chip label={`Deed: ${county.deedFee}`} size="small" sx={{ mr: 1 }} />
                  <Chip label={`Mortgage: ${county.mortgageFee}`} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Recording Office
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {county.recordingOffice}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {county.address}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {county.phone}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Website
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {county.website}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Business Hours
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {county.hours}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Recording Fees
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Deed Recording: {county.deedFee}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Mortgage Recording: {county.mortgageFee}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Special Requirements
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {county.specialRequirements.map((req, reqIndex) => (
                        <Typography component="li" key={reqIndex} variant="body2" sx={{ mb: 0.5 }}>
                          {req}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Fee Schedule Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Recording Fee Quick Reference
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                  <TableCell sx={{ fontWeight: 600 }}>County</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Deed Fee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mortgage Fee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counties.map((county, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 500 }}>{county.name}</TableCell>
                    <TableCell>{county.deedFee}</TableCell>
                    <TableCell>{county.mortgageFee}</TableCell>
                    <TableCell>{county.phone}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Professional Tips */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Professional Tips
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0369a1' }}>
                  Best Practices
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Always call ahead to confirm current fees and requirements
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Keep copies of all recording receipts for client files
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Consider electronic recording where available
                  </Typography>
                  <Typography component="li">
                    Build relationships with county clerk staff
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, backgroundColor: '#f0fdf4' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#16a34a' }}>
                  Time-Saving Tips
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Prepare all documents in advance
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Use expedited service when deadlines are tight
                  </Typography>
                  <Typography component="li" sx={{ mb: 1 }}>
                    Schedule recordings during non-peak hours
                  </Typography>
                  <Typography component="li">
                    Maintain updated contact lists for all counties
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="NJ Recording Requirements Guide"
          resourceType="professional"
          formType="consultation"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
              Need Professional Recording Services?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Let our experienced team handle your recording needs throughout New Jersey.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                  px: 4,
                  py: 1.5,
                }}
              >
                Contact Our Team
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  '&:hover': { borderColor: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' },
                }}
              >
                More Resources
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default NJRecordingRequirements; 